import { makeAutoObservable } from "mobx";
import {
  GetDocumentationStatsWindowEnum,
  PatientTableFilters,
  RxTableFilters,
  RxValidationBulkJobResultDto,
  RxValidationBulkRowDto,
} from "../../api-client";

/**
 * Time window over which per-therapist documentation completeness is
 * computed for the Tagesdoku tab. Aliased from the generated API enum so all
 * call sites use a stable name.
 */
export type DocumentationStatsWindow = GetDocumentationStatsWindowEnum;

/**
 * Subtabs (entities) of the Registry / Register page.
 *
 * The URL value is used as the `tabSelection` search-param so that the user
 * can deep-link to a specific entity tab in the registry.
 */
export enum REGISTRY_SUBTABS {
  PATIENTS = "patients",
  RX = "rx",
  DOCTORS = "doctors",
  DOCTOR_REPORTS = "doctorReports",
  TREATMENT_NOTES = "treatmentNotes",
}

export const REGISTRY_PAGE_SIZE = 50;

/**
 * Status value used to count "unsubmitted" doctor reports in the therapist
 * summary table on the doctor-reports tab.
 *
 * Matches {@link DoctorReportStatusEnum.Draft} on the backend — i.e. the
 * therapist hasn't submitted the report yet. Kept here as a single
 * easy-to-change constant so future enum changes only need one update.
 */
export const UNSUBMITTED_DOCTOR_REPORT_STATUS = "Entwurf" as const;

// ----------------- Filter shapes ------------------------------------------------

/**
 * Patients filters used on the registry Patients tab.
 *
 * Extends the auto-generated PatientTableFilters with the additional client-side
 * fields we need for the registry view (tarif group, copayment exemption status).
 * The unknown extra fields are forwarded to the backend and silently ignored if
 * the backend has not been extended yet.
 */
export interface RegistryPatientFilters extends Omit<PatientTableFilters, "copayment"> {
  tarifGroupId?: string;
  /**
   * - `any`        – no filter applied (default)
   * - `required`   – patient has coPayment === true and has no exemption document
   * - `exempted`   – patient has at least one active copayment exemption
   * - `missingDoc` – patient is marked as having an exemption but the proving
   *                  document is missing (the "should be exempt but isn't documented")
   */
  copayment?: "any" | "required" | "exempted" | "missingDoc";
}

/**
 * Rx filters used on the registry Rx tab.
 *
 * Extends the auto-generated RxTableFilters with tarif group / rx type / account
 * filters. Forwarded to the backend.
 */
export interface RegistryRxFilters extends RxTableFilters {
  tarifGroupId?: string;
  rxTypeId?: string;
  accountId?: string;
}

export interface RegistryDoctorReportFilters {
  limit: number;
  page: number;
  location?: string;
  assignedToUserId?: string; // therapist
  status?: string;
  doctorBsnr?: string;
  accountId?: string;
  sortBy?: Record<string, "ASC" | "DESC">;
}

export interface RegistryTreatmentNoteFilters {
  limit: number;
  page: number;
  location?: string;
  therapistId?: string;
  sortBy?: Record<string, "ASC" | "DESC">;
}

/**
 * Lifecycle status of an on-demand "Rezeptprüfung" (bulk Rx validation) run.
 *
 * Only the two "active" states need to be distinguished by the UI:
 *   - `running` — drives the progress bar / spinner / loading message and locks
 *     the Standort filter.
 *   - `error`   — surfaces an error message in the worklist table.
 *   - `done`    — a run has finished; results (or the empty-state) are shown and
 *     the pill shows a durable "finished" marker (+ enables the refresh button).
 * The neutral resting state (`idle`) covers "never run" / freshly reset.
 */
export type RxValidationStatus = "idle" | "running" | "error" | "done";

// ----------------- Store --------------------------------------------------------

/**
 * Tracks the user's selections on the Registry page:
 *   - which sub-tab is currently active
 *   - per-tab filters and table selection
 *
 * Actual data is fetched and cached via tanstack-query – the store only holds
 * the user's UI selections (matching the store-vs-cache pattern used across
 * the rest of the app, e.g. NavTablesStore).
 */
export default class RegistryStore {
  public constructor() {
    makeAutoObservable(this);
  }

  // ---- Active tab ---------------------------------------------------------
  public activeTab: REGISTRY_SUBTABS = REGISTRY_SUBTABS.PATIENTS;
  setActiveTab = (tab: REGISTRY_SUBTABS) => {
    this.activeTab = tab;
  };

  // ---- Patients tab -------------------------------------------------------
  public patientFilters: RegistryPatientFilters = {
    limit: REGISTRY_PAGE_SIZE,
    location: undefined,
    includeDeleted: false,
    tarifGroupId: undefined,
    copayment: "any",
    sortBy: { patientName: "ASC" },
  };
  public patientSelectedIndex: number = -1;

  setPatientFilters = (
    update:
      | Partial<RegistryPatientFilters>
      | ((prev: RegistryPatientFilters) => Partial<RegistryPatientFilters>),
  ): void => {
    const newParams = typeof update === "function" ? update(this.patientFilters) : update;
    this.patientFilters = { ...this.patientFilters, ...newParams };
  };
  setPatientSelectedIndex = (index: number | ((prev: number) => number)) => {
    this.patientSelectedIndex =
      typeof index === "function" ? index(this.patientSelectedIndex) : index;
  };

  // ---- Rx tab -------------------------------------------------------------
  public rxFilters: RegistryRxFilters = {
    limit: REGISTRY_PAGE_SIZE,
    location: undefined,
    includeDeleted: false,
    rxNumber: "",
    lastName: "",
    processingStatus: undefined,
    doctorReportStatus: undefined,
    tarifGroupId: undefined,
    rxTypeId: undefined,
    accountId: undefined,
    prescribedTherapyId: undefined,
    sortBy: { createdAt: "DESC" }, // default sort: creation date descending
  };
  public rxSelectedIndex: number = -1;

  setRxFilters = (
    update:
      | Partial<RegistryRxFilters>
      | ((prev: RegistryRxFilters) => Partial<RegistryRxFilters>),
  ): void => {
    const newParams = typeof update === "function" ? update(this.rxFilters) : update;
    this.rxFilters = { ...this.rxFilters, ...newParams };
  };
  setRxSelectedIndex = (index: number | ((prev: number) => number)) => {
    this.rxSelectedIndex = typeof index === "function" ? index(this.rxSelectedIndex) : index;
  };

  // ---- Doctor reports tab -------------------------------------------------
  public doctorReportFilters: RegistryDoctorReportFilters = {
    limit: REGISTRY_PAGE_SIZE,
    page: 1,
    location: undefined,
    assignedToUserId: undefined,
    status: undefined,
    doctorBsnr: undefined,
    accountId: undefined,
    sortBy: { createdAt: "DESC" },
  };
  public doctorReportSelectedIndex: number = -1;

  setDoctorReportFilters = (
    update:
      | Partial<RegistryDoctorReportFilters>
      | ((prev: RegistryDoctorReportFilters) => Partial<RegistryDoctorReportFilters>),
  ): void => {
    const newParams =
      typeof update === "function" ? update(this.doctorReportFilters) : update;
    this.doctorReportFilters = { ...this.doctorReportFilters, ...newParams };
  };
  setDoctorReportSelectedIndex = (index: number | ((prev: number) => number)) => {
    this.doctorReportSelectedIndex =
      typeof index === "function" ? index(this.doctorReportSelectedIndex) : index;
  };

  // ---- Treatment notes tab ------------------------------------------------
  public treatmentNoteFilters: RegistryTreatmentNoteFilters = {
    limit: REGISTRY_PAGE_SIZE,
    page: 1,
    location: undefined,
    therapistId: undefined,
    sortBy: { date: "DESC" },
  };
  public treatmentNoteSelectedIndex: number = -1;

  setTreatmentNoteFilters = (
    update:
      | Partial<RegistryTreatmentNoteFilters>
      | ((prev: RegistryTreatmentNoteFilters) => Partial<RegistryTreatmentNoteFilters>),
  ): void => {
    const newParams =
      typeof update === "function" ? update(this.treatmentNoteFilters) : update;
    this.treatmentNoteFilters = { ...this.treatmentNoteFilters, ...newParams };
  };
  setTreatmentNoteSelectedIndex = (index: number | ((prev: number) => number)) => {
    this.treatmentNoteSelectedIndex =
      typeof index === "function" ? index(this.treatmentNoteSelectedIndex) : index;
  };

  /**
   * Time window applied to the per-therapist documentation-completeness
   * stats shown above the Treatment Notes table. Defaults to "last 3 months".
   */
  public documentationStatsWindow: DocumentationStatsWindow =
    GetDocumentationStatsWindowEnum.Last3Months;
  setDocumentationStatsWindow = (window: DocumentationStatsWindow) => {
    this.documentationStatsWindow = window;
  };

  // ---- Rezeptprüfung (on-demand bulk Rx validation) ----------------------
  /**
   * Whether the Rx tab is showing the validation worklist (red-only Rxs)
   * instead of the normal Rx list. Survives in-app navigation so the user can
   * open a flagged Rx in a new tab and come back to the worklist.
   */
  public validationMode = false;
  /** Optional in-mode location filter. Undefined = all locations. */
  public validationLocationId?: string = undefined;
  /** Workflow id of the active/last run, used to re-subscribe (replay) on remount. */
  public validationWorkflowId?: string = undefined;
  /** Accumulated invalid (Not Valid) Rx rows streamed from the backend. */
  public validationRows: RxValidationBulkRowDto[] = [];
  /** Number of Rxs validated so far (live). */
  public validationProcessed = 0;
  /** Total number of Rxs to validate in the current run. */
  public validationTotal = 0;
  /** Smoothed progress (0–100) of the active run, fed by the socket bridge. */
  public validationProgress = 0;
  public validationStatus: RxValidationStatus = "idle";
  public validationSelectedIndex = -1;
  /**
   * Identity of the inputs (Standort + registry filters) the displayed result
   * was computed for. Re-entering validation mode reuses the stored rows, so
   * without this a changed filter would show the previous run's numbers as if
   * they answered the new question (MD-184).
   */
  public validationRunKey?: string = undefined;

  setValidationMode = (on: boolean) => {
    this.validationMode = on;
  };
  setValidationLocationId = (locationId?: string) => {
    this.validationLocationId = locationId;
  };
  setValidationWorkflowId = (workflowId?: string) => {
    this.validationWorkflowId = workflowId;
  };
  setValidationStatus = (status: RxValidationStatus) => {
    this.validationStatus = status;
  };
  setValidationRunKey = (runKey?: string) => {
    this.validationRunKey = runKey;
  };
  setValidationProgress = (progress: number) => {
    this.validationProgress = progress;
  };
  setValidationSelectedIndex = (index: number | ((prev: number) => number)) => {
    this.validationSelectedIndex =
      typeof index === "function" ? index(this.validationSelectedIndex) : index;
  };

  /** Clear accumulated results before a (re)subscribe so replay rebuilds cleanly. */
  resetValidationResults = () => {
    this.validationRows = [];
    this.validationProcessed = 0;
    this.validationTotal = 0;
    this.validationProgress = 0;
    this.validationSelectedIndex = -1;
  };

  /** Append a streamed batch of invalid rows, de-duplicating by Rx id (replay-safe). */
  appendValidationRows = (
    rows: RxValidationBulkRowDto[],
    processed: number,
    total: number,
  ) => {
    if (rows.length > 0) {
      const existing = new Set(this.validationRows.map((r) => r.id));
      const fresh = rows.filter((r) => !existing.has(r.id));
      if (fresh.length > 0) {
        this.validationRows = [...this.validationRows, ...fresh];
      }
    }
    this.validationProcessed = processed;
    this.validationTotal = total;
  };

  /** Replace results with the authoritative final result when the workflow completes. */
  setValidationFinal = (result: RxValidationBulkJobResultDto) => {
    this.validationRows = result.rows;
    this.validationProcessed = result.totalChecked;
    this.validationTotal = result.totalChecked;
  };
}
